import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaTwitter, FaInstagram, FaDiscord } from "react-icons/fa";
import { FiMail, FiMapPin, FiHeart, FiArrowUp, FiZap } from "react-icons/fi";

function Footer() {
    const currentYear = new Date().getFullYear();
    const [isScrolled, setIsScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            setIsScrolled(scrollTop > 300);
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll(); // Check initial state
        
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <footer className="mt-auto relative overflow-hidden">
            {/* Background with gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#ffcccc] via-[#ffe5e5] to-white"></div>
            <div className="absolute inset-0 bg-[#C8102E]/80"></div>
            
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-80"></div>
            
            {/* Content */}
            <div className="relative z-10 border-t border-white/40 shadow-white/20">
            {/* Main Footer Content */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                    {/* Top Section - Brand and Mission */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mb-12 pb-12 border-b border-white/30">
                        {/* Brand Section - Larger */}
                        <div className="lg:col-span-2">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                                <div className="relative">
                                    {/* Glowing halo effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-white/15 to-white/20 rounded-full blur-xl opacity-50"></div>
                                    <div className="relative z-10 w-20 h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 flex items-center justify-center bg-gradient-to-br from-white via-white/90 to-white rounded-2xl shadow-2xl border-2 border-white/60 shadow-white/50">
                                        <svg 
                                            viewBox="0 0 100 100" 
                                            className="w-14 h-14 md:w-16 md:h-16 lg:w-20 lg:h-20"
                                            style={{
                                                filter: 'drop-shadow(0 0 10px rgba(15, 31, 10, 0.3))'
                                            }}
                                        >
                                            {/* Wave/Tide Symbol */}
                                            <path 
                                                d="M20 50 Q30 30, 40 50 T60 50 T80 50" 
                                                stroke="#C8102E" 
                                                strokeWidth="8" 
                                                fill="none" 
                                                strokeLinecap="round"
                                                className="animate-pulse"
                                            />
                                            <path 
                                                d="M25 65 Q35 45, 45 65 T65 65 T85 65" 
                                                stroke="#C8102E" 
                                                strokeWidth="8" 
                                                fill="none" 
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div>
                                    <h2 className="font-bold text-3xl text-white mb-2 drop-shadow-md">KidKat Games</h2>
                                    <div className="w-16 h-0.5 bg-gradient-to-r from-white via-white/80 to-white rounded-full shadow-lg shadow-white/50"></div>
                                </div>
                        </div>
                            <p className="text-white text-base leading-relaxed mb-4 max-w-2xl">
                            Creating mobile gaming experiences that inspire, connect, and open new worlds to players worldwide.
                        </p>
                            <div className="flex items-center gap-2 text-sm text-white/90">
                            <span>Built with</span>
                            <FiHeart className="text-red-400 animate-pulse" />
                            <span>by passionate developers</span>
                        </div>
                    </div>

                        {/* Quick Contact Section */}
                        <div className="lg:col-span-1">
                            <h3 className="font-bold text-white mb-6 text-lg uppercase tracking-wide">Get in Touch</h3>
                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-white/20 rounded-lg border border-white/40 shadow-lg shadow-white/20 flex-shrink-0">
                                        <FiMail className="text-white text-lg" />
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className="font-semibold text-white mb-1 text-sm">Email</h4>
                                        <a
                                            href="mailto:contact@kidkats.com"
                                            className="text-white/90 hover:text-white transition-colors text-sm block break-all"
                                        >
                                            contact@kidkats.com
                                        </a>
                                        <p className="text-xs text-white/80 mt-1">
                                            Response within 24-48h
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Section - Links Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
                    {/* Quick Links */}
                    <div>
                            <h3 className="font-bold text-white mb-4 text-base uppercase tracking-wide border-b border-white/40 pb-2">Navigation</h3>
                            <ul className="space-y-2.5 mt-4">
                                <li>
                                    <Link 
                                        to="/" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                    Home
                                </Link>
                            </li>
                            <li>
                                    <Link 
                                        to="/about" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                    About Us
                                </Link>
                            </li>
                            <li>
                                    <Link 
                                        to="/contact" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                    Contact
                                </Link>
                            </li>
                        </ul>
                    </div>

                    {/* Legal & Policies */}
                    <div>
                            <h3 className="font-bold text-white mb-4 text-base uppercase tracking-wide border-b border-white/40 pb-2">Legal</h3>
                            <ul className="space-y-2.5 mt-4">
                                <li>
                                    <Link 
                                        to="/terms" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                    Terms of Service
                                </Link>
                            </li>
                            <li>
                                    <Link 
                                        to="/privacy" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                    Privacy Policy
                                </Link>
                            </li>
                            <li>
                                    <Link 
                                        to="/unsubscribe" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                    Unsubscribe
                                </Link>
                            </li>
                        </ul>
                    </div>

                        {/* Resources Section */}
                    <div>
                            <h3 className="font-bold text-white mb-4 text-base uppercase tracking-wide border-b border-white/40 pb-2">Resources</h3>
                            <ul className="space-y-2.5 mt-4">
                                <li>
                                    <a 
                                        href="/contact" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                        Support Center
                                    </a>
                                </li>
                                <li>
                                    <a 
                                        href="/about" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                        Our Story
                                    </a>
                                </li>
                                <li>
                                    <a 
                                        href="/contact" 
                                        className="text-white/90 hover:text-white transition-colors text-sm flex items-center gap-2 group"
                                    >
                                        <span className="w-1.5 h-1.5 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg shadow-white/50"></span>
                                        Partnership
                                    </a>
                                </li>
                            </ul>
                                </div>

                        {/* Newsletter/CTA Section */}
                                <div>
                            <h3 className="font-bold text-white mb-4 text-base uppercase tracking-wide border-b border-white/40 pb-2">Stay Connected</h3>
                            <p className="text-white/90 text-sm mb-4 mt-4 leading-relaxed">
                                Follow us for the latest updates, game releases, and exclusive content.
                            </p>
                            <div className="flex flex-col gap-3">
                                <Link
                                    to="/contact"
                                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-white/90 rounded-lg text-[#C8102E] font-bold text-sm transition-all shadow-xl shadow-white/40 hover:shadow-2xl hover:shadow-white/60 hover:scale-105"
                                >
                                    <FiMail />
                                    Subscribe
                                </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
                <div className="border-t border-white/40 bg-[#C8102E]/90 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        {/* Copyright */}
                            <div className="text-sm text-white/90 text-center sm:text-left order-2 sm:order-1">
                            <p>
                                © {currentYear} KidKat Games. All rights reserved.
                            </p>
                        </div>
                            </div>
                    </div>
                </div>
            </div>

            {/* Floating Scroll to Top Button - Visible when scrolled */}
            {isScrolled && (
                <button
                    onClick={scrollToTop}
                     className="fixed bottom-8 right-8 z-50 flex items-center gap-2 px-4 py-2 rounded-full bg-white/30 hover:bg-white/50 border border-white/60 text-[#C8102E] hover:text-[#A00D25] transition-all text-sm shadow-lg shadow-white/30 hover:shadow-xl hover:shadow-white/40 hover:scale-105 animate-in fade-in slide-in-from-bottom-4"
                    aria-label="Scroll to top"
                    title="Scroll to top"
                >
                    <FiArrowUp />
                    <span className="hidden sm:inline">Back to Top</span>
                </button>
            )}
        </footer>
    );
}

export default Footer;
